'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Fragment, useEffect, useRef } from 'react';
import { Divider, Slider } from 'ui';
import { Pagination } from 'ui/components/base/pagination';

import { useTranslation } from '@/i18n/client';
import { viewProductListPageEvent } from '@/modules/google/data-layer/data-layer';
import { BannersAsProductTile } from '@/modules/product/components/banners/BannersAsProductTile';
import { FullWidthBanners } from '@/modules/product/components/banners/FullWidthBanners';
import { CategoryThreeNav } from '@/modules/product/components/productsList/categoryThreeNav';
import { CategoryThreeSidebar } from '@/modules/product/components/productsList/categoryThreeSidebar';
import { CollectionItem } from '@/modules/product/components/productsList/CollectionItem';
import { EmptyList } from '@/modules/product/components/productsList/emptyList';
import { FilterBar } from '@/modules/product/components/productsList/filterBar';
import { ProductSkeleton } from '@/modules/product/components/productsList/productSkeleton';
import { ProductTile } from '@/modules/product/components/productsList/productTile';
import { useProductList } from '@/modules/product/context/productList';
import { routes } from '@/shared/data/routes';
import { useCurrentLocale } from '@/shared/utils/client';
import { numberToIntervalCount } from '@/shared/utils/number-to-interval-count';

import styles from './ProductsListPage.module.scss';
import { listCategoriesBySlug } from './utils';

export const MAX_EVENT_PRODUCTS = 8;

export const ProductsListPage = () => {
  const {
    breadcrumbs,
    banners,
    bannersState,
    page,
    products,
    total,
    limit,
    isLoading,
    categorySlug,
    brandName,
    categoryThree,
    query,
    seo,
    crossRewrite,
    handleFetchNewPage,
    isRewritePage,
  } = useProductList();
  const { t } = useTranslation(['products', 'common']);
  const container = useRef<HTMLDivElement>(null);
  const locale = useCurrentLocale();
  const pathname = usePathname();

  const clearedSlug = categorySlug.includes('%')
    ? categorySlug.split('%')[0]
    : categorySlug;

  const { listCategories } = listCategoriesBySlug(clearedSlug, categoryThree);

  let listId = brandName
    ? 'Lista produktów MARKA'
    : categorySlug === 'search'
      ? 'Lista produktów WYSZUKIWARKA'
      : 'Lista produktów KATEGORIA';

  if (isRewritePage) {
    listId = 'Lista produktów REWRITE';
  }

  const listName = brandName
    ? brandName
    : categorySlug === 'search'
      ? 'Wyniki wyszukiwania'
      : (breadcrumbs[breadcrumbs.length - 1]?.name ?? '');

  const handlePageChange = async (newPage: number) => {
    handleFetchNewPage(newPage);

    window.document.scrollingElement?.scrollTo(0, 0);
  };

  useEffect(() => {
    if (products.length > 0) {
      viewProductListPageEvent(
        products,
        listId,
        listName,
        products[0].currency,
        { page: page, limit: limit }
      );
    }
  }, [products]);

  return (
    <>
      <div className={styles.productsListPage} ref={container}>
        <div className={styles.productsListPageContainer}>
          <div
            className={styles.productsListPageInfo}
            style={{
              paddingBottom: categorySlug === 'search' ? 20.8 : 12.8,
            }}
          >
            <div className={styles.productsListPageHeading}>
              <div className="container">
                <div className={styles.productsListPageHeadingTitle}>
                  <h1>
                    {seo?.topDescription?.h1
                      ? seo.topDescription.h1
                      : categorySlug === 'search'
                        ? t('productList.searchTitle', { query })
                        : pathname?.includes(routes[locale].SINGLE_BRAND)
                          ? brandName
                          : (breadcrumbs[breadcrumbs.length - 1]?.name ?? '')}
                  </h1>
                  <span>
                    (
                    {t('productList.inOfferProducts_interval', {
                      postProcess: 'interval',
                      count: numberToIntervalCount(total),
                      amount: total,
                    })}
                    )
                  </span>
                </div>
                {categorySlug !== 'search' && (
                  <ol className={styles.productsListPageHeadingBreadcrumbs}>
                    <li>
                      <Link href="/">{t('common.org.name')}</Link>
                    </li>
                    {breadcrumbs.map((breadcrumb, index) => (
                      <li key={index}>
                        <Link
                          className={
                            styles.productsListPageHeadingBreadcrumbsLink
                          }
                          href={
                            breadcrumb.slug.startsWith('/')
                              ? breadcrumb.slug
                              : `${routes[locale].PRODUCTS_LIST}/${breadcrumb.slug}`
                          }
                          prefetch={false}
                        >
                          {breadcrumb.name}
                        </Link>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          </div>

          {seo && seo.topDescription.popularCollections.length > 0 && (
            <div className={styles.productsListPagePopularCollection}>
              <Slider
                slidesPerView="auto"
                slidesPerGroup={1}
                allowSwipe
                hasArrows
                spaceBetween={16}
                watchOverflow
                loop
              >
                {seo.topDescription.popularCollections.map(
                  (collection, index) => (
                    <CollectionItem
                      key={index}
                      image={collection.image}
                      name={collection.name}
                      url={collection.url}
                    />
                  )
                )}
              </Slider>
            </div>
          )}

          <div className={styles.productsListPageGridWrapper}>
            <div className="container">
              <FilterBar hasCategories={listCategories.length > 0} />
              <div className={styles.productsListPageGridMenuWrapper}>
                {categorySlug === 'search' ||
                categorySlug === 'promotions' ||
                categorySlug === 'sellouts' ||
                pathname?.includes(routes[locale].SINGLE_BRAND) ? null : (
                  <CategoryThreeNav />
                )}
                <div className={styles.productsListPageGrid}>
                  {isLoading ? (
                    Array.from(Array(limit).keys()).map((n) => (
                      <ProductSkeleton key={n} />
                    ))
                  ) : products.length > 0 ? (
                    products.map((product, index) => (
                      <Fragment key={index}>
                        {index === 14 &&
                          !!banners.product_grid_pos_15.length && (
                            <BannersAsProductTile
                              banners={banners.product_grid_pos_15}
                              name="product_grid_pos_15"
                              initialSlide={bannersState.product_grid_pos_15}
                            />
                          )}
                        {index === 37 &&
                          !!banners.product_grid_pos_39.length && (
                            <BannersAsProductTile
                              banners={banners.product_grid_pos_39}
                              name="product_grid_pos_39"
                              initialSlide={bannersState.product_grid_pos_39}
                            />
                          )}
                        <ProductTile
                          key={product.productId}
                          index={index}
                          product={product}
                          listId={listId}
                          listName={listName}
                        />
                        {index === 22 && !!banners.inside_grid.length && (
                          <FullWidthBanners
                            maxHeight={300}
                            banners={banners['inside_grid']}
                            name="inside_grid"
                            initialSlide={bannersState.inside_grid}
                          />
                        )}
                      </Fragment>
                    ))
                  ) : (
                    <EmptyList />
                  )}
                </div>
              </div>

              {products.length > 0 ? (
                <Divider disableBackground={false} />
              ) : null}

              <div className={styles.productsListPagePagination}>
                {limit > 1 && (
                  <Pagination
                    onPageChange={handlePageChange}
                    totalCount={total}
                    showProgress={true}
                    currentPage={page}
                    pageSize={limit}
                    previousPageLabel={t('common:common.previousPage')}
                    nextPageLabel={t('common:common.nextPage')}
                    progressLabel={({ currentCount, totalCount }) =>
                      t('productList.pagination', { currentCount, totalCount })
                    }
                  />
                )}
              </div>

              {crossRewrite.length > 0 ? (
                <div className={styles.productsListPageCrossRewrites}>
                  <h3>
                    {brandName
                      ? `Popularne produkty ${brandName}`
                      : t('productList.popularInCategory', {
                          category:
                            breadcrumbs[breadcrumbs.length - 1]?.name ?? '',
                        })}
                  </h3>
                  <div className={styles.productsListPageCrossRewritesWrapper}>
                    {crossRewrite.map((item, index) => (
                      <Link href={item.url} key={index}>
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : null}

              <>
                {seo?.bottomDescription ? (
                  <div
                    className={styles.productsListPageBottomDescription}
                    dangerouslySetInnerHTML={{ __html: seo.bottomDescription }}
                  ></div>
                ) : null}
              </>

              {isLoading ? null : (
                <FullWidthBanners
                  maxHeight={300}
                  banners={banners['above_footer']}
                  name="above_footer"
                  initialSlide={bannersState.above_footer}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {categorySlug === 'search' ||
      pathname?.includes(routes[locale].SINGLE_BRAND) ? null : (
        <CategoryThreeSidebar />
      )}
    </>
  );
};
